"use client"

import * as React from "react"
import { Sparkles, X as LucideX } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "../../lib/utils"
import { Button } from "./button"

export interface OnboardingTutorialCardProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description: React.ReactNode
  primaryButtonText?: string
  onPrimaryButtonClick?: () => void
  secondaryButtonText?: string
  onSecondaryButtonClick?: () => void
  arrowLeftOffset?: string // e.g. "left-[15%] sm:left-[22%]"
  icon?: React.ReactNode
  backdropClassName?: string
  cardClassName?: string
  containerClassName?: string // for the fixed outer container
  animationPreset?: "breathing-float" | "gentle-sway" | "pulse" | "subtle-bounce" | "none"
}

const ANIMATION_PRESETS = {
  "breathing-float": {
    animate: {
      y: [0, -6, 0],
      scale: [1, 1.012, 1],
      rotate: [-0.4, 0.4, -0.4],
    },
    transition: {
      repeat: Infinity,
      duration: 5,
      ease: "easeInOut",
    },
  },
  "gentle-sway": {
    animate: {
      x: [0, 4, -4, 0],
      y: [0, -4, 0],
      rotate: [-1.2, 1.2, -1.2],
    },
    transition: {
      repeat: Infinity,
      duration: 6,
      ease: "easeInOut",
    },
  },
  pulse: {
    animate: {
      scale: [1, 1.025, 1],
    },
    transition: {
      repeat: Infinity,
      duration: 3,
      ease: "easeInOut",
    },
  },
  "subtle-bounce": {
    animate: {
      y: [0, -6, 0],
    },
    transition: {
      repeat: Infinity,
      duration: 3,
      ease: "easeInOut",
    },
  },
  none: {
    animate: {},
    transition: {},
  },
}

function OnboardingTutorialCard({
  isOpen,
  onClose,
  title,
  description,
  primaryButtonText,
  onPrimaryButtonClick,
  secondaryButtonText = "Dismiss",
  onSecondaryButtonClick,
  arrowLeftOffset = "left-1/2",
  icon = <Sparkles className="size-4 animate-pulse" />,
  backdropClassName,
  cardClassName,
  containerClassName,
  animationPreset = "none",
}: OnboardingTutorialCardProps) {
  const handleSecondaryClick = onSecondaryButtonClick || onClose
  const preset = ANIMATION_PRESETS[animationPreset] || ANIMATION_PRESETS["none"]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Dimmer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className={cn(
              "fixed inset-0 bg-black/40 z-49",
              backdropClassName
            )}
          />

          {/* Onboarding Dialog Card Container */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95, x: "-50%" }}
            animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
            exit={{ opacity: 0, y: 20, scale: 0.95, x: "-50%" }}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
            className={cn(
              "fixed bottom-[104px] left-1/2 z-50 w-[calc(100vw-32px)] max-w-[320px]",
              containerClassName
            )}
          >
            {/* Floating Animation Wrapper */}
            <motion.div
              animate={preset.animate}
              transition={preset.transition}
              className={cn(
                "relative w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-lg text-left",
                cardClassName
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  {icon && (
                    <div className="p-1.5 rounded-lg bg-[#FFF2F9] text-[#ED5AA9] border border-black/10 shrink-0">
                      {icon}
                    </div>
                  )}
                  <h4 className="font-bold text-sm uppercase tracking-wider text-[#101828]">
                    {title}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors p-0.5 rounded cursor-pointer"
                  aria-label="Close onboarding tutorial"
                >
                  <LucideX className="size-4" />
                </button>
              </div>

              <div className="mt-2.5 text-xs text-[#475467] leading-relaxed">
                {description}
              </div>

              {primaryButtonText && (
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    onClick={onPrimaryButtonClick}
                    size="sm"
                    className="flex-1 text-xs"
                  >
                    {primaryButtonText}
                  </Button>
                  {secondaryButtonText && (
                    <Button
                      onClick={handleSecondaryClick}
                      variant="ghost"
                      size="sm"
                      className="text-xs font-semibold text-[#667085] hover:text-[#344054] py-2 px-3 cursor-pointer"
                    >
                      {secondaryButtonText}
                    </Button>
                  )}
                </div>
              )}

              {/* Integrated Seamless speech bubble beak pointing directly at target icon */}
              <div
                className={cn(
                  "absolute bottom-[-9px] -translate-x-1/2 w-4 h-4 bg-white border-r border-b border-gray-200 rotate-45 z-10",
                  arrowLeftOffset
                )}
              />
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export { OnboardingTutorialCard }
